import { inject, Injectable, signal } from '@angular/core';
import { getToken, Messaging, onMessage } from '@angular/fire/messaging'; // Use compat for easier usage with older patterns if needed, or non-compat for new API
import { liveQuery } from 'dexie';
import { appDb } from 'dexie/app.db';
import {
  catchError,
  EMPTY,
  from,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { NotificationApiService } from './api/notification-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DBService } from './db.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  protected dbService = inject(DBService);
  protected notifactionApiService = inject(NotificationApiService);
  public messages = signal([]);

  protected messaging = inject(Messaging);

  private get activeToken() {
    return localStorage.getItem('fcmToken') || '';
  }

  private set activeToken(val: string) {
    localStorage.setItem('fcmToken', val);
  }

  constructor() {
    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received:', payload);
      this.messages.set([payload]);
      // You might want to show a UI notification here
    });
  }

  getList() {
    return from(
      liveQuery(() => appDb.notification.reverse().sortBy('createdAt'))
    );
  }

  markAllAsRead() {
    return from(
      liveQuery(() =>
        appDb.notification.where(['isDeleted', 'read']).equals([0, 0]).toArray()
      )
    ).pipe(
      switchMap((notifications) => {
        if (!notifications.length) {
          return of(null);
        }
        return this.notifactionApiService.readAll(notifications).pipe(
          catchError((err: HttpErrorResponse) => {
            if (!navigator.onLine) {
              return this.dbService.bulkUpdateRow(
                appDb.notification,
                notifications.map((item) => ({
                  id: item.id,
                  read: 1,
                  isSynced: 0,
                }))
              );
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  read(id) {
    return this.notifactionApiService.read(id).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.notification, {
            id,
            read: 1,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  delete(id) {
    return this.notifactionApiService.delete(id).pipe(
      switchMap((res: any) => this.dbService.deleteRow(appDb.notification, id)),
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.notification, {
            id,
            isDeleted: 1,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  async requestPermissionAndGetToken() {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      from(
        navigator.serviceWorker.ready.then(
          async (serviceWorkerRegistration) => {
            const currentToken = await getToken(this.messaging, {
              serviceWorkerRegistration: serviceWorkerRegistration,
              vapidKey: environment.vapidKey,
            });
            // console.log('Permission granted! FCM Token:', currentToken);
            return currentToken;
          }
        )
      )
        .pipe(
          switchMap((token) => {
            if (token === this.activeToken) {
              return of(null);
            }
            this.activeToken = token;
            return this.notifactionApiService.addDevice(token);
          })
        )
        .subscribe();
      // Notification permission granted.
    } else {
    }
  }

  public requestNotifications(): void {
    this.requestPermissionAndGetToken();
  }
}
